import Component from '@glimmer/component';
import { service } from '@ember/service';
import { scheduleOnce } from '@ember/runloop';
import type Owner from '@ember/owner';
import type TourManagerService from 'tio-common/services/tour-manager';

/**
 * Component that automatically triggers a tour when it renders.
 * Designed to work with database-driven tour configurations.
 *
 * Usage:
 * ```gts
 * <TourTrigger @tourId="advanced-filters-moved" @autoStart={{true}} />
 * ```
 */
interface TourTriggerSignature {
  Element: HTMLDivElement;
  Args: {
    /**
     * The unique ID of the tour to trigger.
     * In the future, this would reference a database record.
     */
    tourId: string;

    /**
     * Whether to automatically start the tour when the component renders.
     * Default: true
     */
    autoStart?: boolean;

    /**
     * Delay in milliseconds before starting the tour.
     * Useful for waiting for animations or DOM to settle.
     * Default: 100ms
     */
    delay?: number;

    /**
     * Force the tour to show even if it's been completed before.
     * Default: false
     */
    force?: boolean;
  };
}

export default class TourTriggerComponent extends Component<TourTriggerSignature> {
  @service declare tourManager: TourManagerService;

  constructor(owner: Owner, args: TourTriggerSignature['Args']) {
    super(owner, args);

    const autoStart = args.autoStart ?? true;
    if (autoStart && this.tourManager.shouldShowTour(args.tourId)) {
      this.scheduleTour();
    }
  }

  private scheduleTour() {
    const delay = this.args.delay ?? 100;

    // eslint-disable-next-line ember/no-runloop, ember/no-incorrect-calls-with-inline-anonymous-functions
    scheduleOnce('afterRender', this, () => {
      setTimeout(() => {
        this.tourManager.startTour(this.args.tourId, this.args.force ?? false);
      }, delay);
    });
  }

  <template>{{! This component doesn't render anything visible }}</template>
}
